import { missingField } from 'app/constants';
import { GetMomentOfDate } from './moment';

export const getDateValue = (offsetDays, addEndValue = false, value?) => {
  let date;
  if (value) date = new Date(value);
  else date = new Date();
  if (offsetDays) date.setDate(date.getDate() + offsetDays);
  if (addEndValue) date.setHours(23, 59, 59, 999);
  else date.setHours(0, 0, 0, 0);
  return date;
};

export const getFirstOfMonth = (nextMonth = false) => {
  // January - December : 0 - 11
  let date = new Date();
  date = new Date(date.getFullYear(), date.getMonth() + (!nextMonth ? 0 : 1));
  date.setHours(0, 0, 0, 0);
  return date;
};

export const getLastOfMonth = (nextMonth = false) => {
  // January - December : 0 - 11
  let date = new Date();
  date = new Date(date.getFullYear(), date.getMonth() + (!nextMonth ? 1 : 2), 0);
  date.setHours(23, 59, 59, 999);
  return date;
};

export const getAmnStartOfWeek = () => {
  const today = new Date().getDay();
  const offset = today !== 6 ? -(today + 1) : 0;
  return getDateValue(offset);
};

export const getAmnStartOfMonth = () => {
  const date = new Date();
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  return firstDay;
};

export const getAllRangeStart = () => {
  const firstDay = new Date().setFullYear(
    new Date().getFullYear() - 10,
    new Date().getMonth(),
    new Date().getDate() + 1,
  );
  return firstDay;
};

export const getAllRangeEnd = () => {
  const firstDay = new Date().setFullYear(new Date().getFullYear() + 10);
  return firstDay;
};

export const getAmnEndOfMonth = () => {
  const date = new Date();
  var lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return lastDay;
};

export const getAmnEndOfWeek = () => {
  const today = new Date().getDay();
  const offset = today !== 6 ? -(today - 5) : 0;
  return getDateValue(offset, true);
};

/** Fiscal year runs Apr 1 - Mar 31. Returns { startDate, endDate } for current FY in ISO string format. */
export const getCurrentFiscalYearRange = (): { startDate: string; endDate: string } => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-11
  // FY starts April (month 3); Jan-Mar belong to previous FY end
  const fyStartYear = month >= 3 ? year : year - 1;
  const startDate = new Date(fyStartYear, 3, 1); // Apr 1
  const endDate = new Date(fyStartYear + 1, 2, 31, 23, 59, 59, 999); // Mar 31
  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

export type FiscalPeriodKey = '12' | 'Q1' | 'Q2' | 'Q3' | 'Q4';

/** Get current fiscal year start (e.g. Apr 2024 -> Mar 2025 means fyStartYear 2024). */
export const getCurrentFiscalYearStart = (): number => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  return month >= 3 ? year : year - 1;
};

/** Get FY start year from a date string (e.g. Apr 2024 or Jan 2025 -> 2024). */
export const getFiscalYearStartFromDate = (dateStr: string): number => {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = d.getMonth();
  return month < 3 ? year - 1 : year;
};

/** Fiscal year Apr 1 - Mar 31. Quarters: Q1 Apr-Jun, Q2 Jul-Sep, Q3 Oct-Dec, Q4 Jan-Mar. */
export const getFiscalPeriodDateRange = (
  period: FiscalPeriodKey,
  fyStartYear?: number,
): { startDate: string; endDate: string } => {
  const effectiveFyStartYear =
    fyStartYear ?? getCurrentFiscalYearStart();

  if (period === '12') {
    const startDate = new Date(effectiveFyStartYear, 3, 1); // Apr 1
    const endDate = new Date(effectiveFyStartYear + 1, 2, 31, 23, 59, 59, 999); // Mar 31
    return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
  }

  const ranges: Record<Exclude<FiscalPeriodKey, '12'>, { startMonth: number; startDay: number; endMonth: number; endDay: number }> = {
    Q1: { startMonth: 3, startDay: 1, endMonth: 5, endDay: 30 },   // Apr 1 - Jun 30
    Q2: { startMonth: 6, startDay: 1, endMonth: 8, endDay: 30 },   // Jul 1 - Sep 30
    Q3: { startMonth: 9, startDay: 1, endMonth: 11, endDay: 31 },   // Oct 1 - Dec 31
    Q4: { startMonth: 0, startDay: 1, endMonth: 2, endDay: 31 },   // Jan 1 - Mar 31 (next FY year)
  };
  const r = ranges[period];
  const useNextYear = period === 'Q4';
  const startDate = new Date(effectiveFyStartYear + (useNextYear ? 1 : 0), r.startMonth, r.startDay);
  const endDate = new Date(effectiveFyStartYear + (useNextYear ? 1 : 0), r.endMonth, r.endDay, 23, 59, 59, 999);
  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

export const getDateRangeLabel = (
  startDate: string | Date,
  endDate: string | Date,
  startDefault = 'Unknown',
  endDefault = 'Unknown',
  nullValue = missingField,
) => {
  const startMoment = GetMomentOfDate(startDate);
  const endMoment = GetMomentOfDate(endDate);
  return startMoment.isValid() && endMoment.isValid()
    ? `${startMoment.format('MM/DD/YYYY')} - ${endMoment.format('MM/DD/YYYY')}`
    : startMoment.isValid() && !endMoment.isValid()
    ? `${startMoment.format('MM/DD/YYYY')} - ${endDefault}`
    : !startMoment.isValid() && endMoment.isValid()
    ? `${startDefault} - ${endMoment.format('MM/DD/YYYY')}`
    : nullValue;
};

export const getDateRangeLabelWithNull = (
  startDate: string | Date,
  endDate: string | Date,
  startDefault = 'Unknown',
  endDefault = 'Unknown',
  nullValue = missingField,
) => {
  const startMoment = GetMomentOfDate(startDate);
  const endMoment = GetMomentOfDate(endDate);
  if (startDate && endDate) {
    return startMoment.isValid() && endMoment.isValid()
      ? `${startMoment.format('MM/DD/YYYY')} - ${endMoment.format('MM/DD/YYYY')}`
      : startMoment.isValid() && !endMoment.isValid()
      ? `${startMoment.format('MM/DD/YYYY')} - ${endDefault}`
      : !startMoment.isValid() && endMoment.isValid()
      ? `${startDefault} - ${endMoment.format('MM/DD/YYYY')}`
      : nullValue;
  } else if (startDate) {
    return startMoment.isValid()
      ? `${startMoment.format('MM/DD/YYYY')}`
      : startMoment.isValid()
      ? `${startMoment.format('MM/DD/YYYY')}`
      : !startMoment.isValid()
      ? `${startDefault}`
      : nullValue;
  } else if (endDate) {
    return endMoment.isValid()
      ? `${endMoment.format('MM/DD/YYYY')}`
      : !endMoment.isValid()
      ? `${endDefault}`
      : endMoment.isValid()
      ? `${endMoment.format('MM/DD/YYYY')}`
      : nullValue;
  }
};
